import { createElement, useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useBanners } from '../context/BannerContext';
import { InstallBanner } from '../components/ui/InstallBanner';
import { getPwaState, isIOS, promptInstall, subscribePwa } from '../lib/pwa';

const BANNER_ID = 'pwa-install';
const DISMISS_KEY = 'iomi_install_banner_dismissed_at';
/** After "Not now", stay quiet for this long before offering again. */
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

function isSnoozed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    return Number.isFinite(at) && Date.now() - at < SNOOZE_MS;
  } catch {
    return false;
  }
}

function snooze() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Storage unavailable (private mode) — banner simply returns next load.
  }
}

/**
 * Offers to install the app as a PWA, once the user is signed in.
 *
 * - Already running from the home screen → never shown.
 * - Chromium (Android/desktop) → shown when the browser fires
 *   `beforeinstallprompt`; the Install button triggers the native dialog.
 * - iOS → shown with manual Share → Add to Home Screen instructions.
 * - Dismissing snoozes it for 30 days; installing hides it for good.
 */
export function useInstallBanner() {
  const { addBanner, removeBanner } = useBanners();
  const { canPromptInstall, isStandalone } = useSyncExternalStore(subscribePwa, getPwaState, getPwaState);
  const [snoozed, setSnoozed] = useState(isSnoozed);

  const mode: 'native' | 'ios' | null = isStandalone || snoozed
    ? null
    : canPromptInstall
      ? 'native'
      : isIOS()
        ? 'ios'
        : null;

  const onDismiss = useCallback(() => {
    snooze();
    setSnoozed(true);
  }, []);

  const onInstall = useCallback(async () => {
    const outcome = await promptInstall();
    // Declining the native dialog counts as "not now" so we don't nag.
    if (outcome === 'dismissed') onDismiss();
  }, [onDismiss]);

  useEffect(() => {
    if (!mode) {
      removeBanner(BANNER_ID);
      return;
    }
    addBanner({
      id: BANNER_ID,
      priority: 20,
      content: createElement(InstallBanner, { mode, onInstall, onDismiss }),
    });
    return () => removeBanner(BANNER_ID);
  }, [mode, onInstall, onDismiss, addBanner, removeBanner]);
}
